import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { getRef } from '../lib/referral';
import { Loader2, Lock, CheckCircle2, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import { RoiEngine } from '../components/RoiEngine';
import PhoneSimulation from '../components/PhoneSimulation';
import { HumanCheck } from '../components/HumanCheck';

const API = process.env.REACT_APP_BACKEND_URL;

const TXT = {
  en: {
    kicker: 'RESTAURANT OPERATING SCORE™', h1: 'One number. Your whole restaurant.',
    sub: 'We scan your real web presence live — AI readiness, Google visibility, menu, conversion — and give you a single score. Like a credit score, for your restaurant.',
    name: 'Restaurant name', website: 'Website (e.g. myrestaurant.com)', city: 'City (optional)',
    cta: 'Get my Score — free, 30 seconds', scanning: 'Scanning your live web presence…',
    steps: ['Fetching your website…', 'Reading structured data (schema.org)…', 'Checking if AI can read your menu…', 'Measuring speed & mobile readiness…', 'Looking for ordering, reservations & socials…'],
    yourScore: 'Restaurant Operating Score™', of: '/ 100',
    labels: { ai_readiness: 'AI Visibility', website_seo: 'Website & SEO', content_conversion: 'Content & Conversion', reputation_google: 'Google & Reputation' },
    pending: 'Pending activation', pendingHint: 'Google reviews & competitor benchmark — activated with your full audit.',
    grade: (s) => (s >= 85 ? 'Excellent' : s >= 70 ? 'Good — with quick wins available' : s >= 50 ? 'At risk — losing diners today' : 'Critical — invisible to AI search'),
    topIssues: 'What we found (live scan)', lockedTitle: (n) => `Unlock the full report — ${n} checks + action plan`,
    lockedSub: 'See every finding, what it costs you, and the exact fixes in priority order. Free.',
    yourName: 'Your name', yourEmail: 'Work email', unlock: 'Unlock my full report',
    fullReport: 'Full report', actionPlan: 'Your priority action plan', impact: { high: 'High impact', medium: 'Medium impact', low: 'Low impact' },
    effort: { '5 minutes': '5 min fix', '1 day': '1 day fix', '1 week': '1 week fix', urgent: 'Urgent' },
    finalCta: 'Want us to fix this for you?', finalSub: 'The Kitxens AI Team can execute this plan — most items within the first weeks.',
    talk: 'Talk to Kitxens on WhatsApp', audit: 'Book my free audit', again: 'Scan another restaurant',
    err: 'Could not complete the scan. Check the website address and try again.',
  },
  es: {
    kicker: 'RESTAURANT OPERATING SCORE™', h1: 'Un número. Todo tu restaurante.',
    sub: 'Escaneamos tu presencia web real en vivo — visibilidad IA, Google, menú, conversión — y te damos una sola puntuación. Como un score crediticio, para tu restaurante.',
    name: 'Nombre del restaurante', website: 'Web (ej. mirestaurante.com)', city: 'Ciudad (opcional)',
    cta: 'Ver mi Score — gratis, 30 segundos', scanning: 'Escaneando tu presencia web en vivo…',
    steps: ['Cargando tu web…', 'Leyendo datos estructurados (schema.org)…', 'Comprobando si la IA puede leer tu menú…', 'Midiendo velocidad y móvil…', 'Buscando pedidos, reservas y redes…'],
    yourScore: 'Restaurant Operating Score™', of: '/ 100',
    labels: { ai_readiness: 'Visibilidad IA', website_seo: 'Web y SEO', content_conversion: 'Contenido y Conversión', reputation_google: 'Google y Reputación' },
    pending: 'Pendiente de activar', pendingHint: 'Reseñas de Google y comparación con competidores — se activa con tu auditoría completa.',
    grade: (s) => (s >= 85 ? 'Excelente' : s >= 70 ? 'Bien — con mejoras rápidas disponibles' : s >= 50 ? 'En riesgo — hoy pierdes comensales' : 'Crítico — invisible para la búsqueda con IA'),
    topIssues: 'Lo que encontramos (escaneo en vivo)', lockedTitle: (n) => `Desbloquea el informe completo — ${n} comprobaciones + plan de acción`,
    lockedSub: 'Ve cada hallazgo, lo que te cuesta, y las soluciones exactas por orden de prioridad. Gratis.',
    yourName: 'Tu nombre', yourEmail: 'Email de trabajo', unlock: 'Desbloquear mi informe completo',
    fullReport: 'Informe completo', actionPlan: 'Tu plan de acción prioritario', impact: { high: 'Impacto alto', medium: 'Impacto medio', low: 'Impacto bajo' },
    effort: { '5 minutes': 'Arreglo en 5 min', '1 day': 'Arreglo en 1 día', '1 week': 'Arreglo en 1 semana', urgent: 'Urgente' },
    finalCta: '¿Quieres que lo arreglemos por ti?', finalSub: 'El AI Team de Kitxens puede ejecutar este plan — la mayoría en las primeras semanas.',
    talk: 'Hablar con Kitxens por WhatsApp', audit: 'Reservar mi auditoría gratis', again: 'Escanear otro restaurante',
    err: 'No pudimos completar el escaneo. Revisa la dirección de la web e inténtalo de nuevo.',
  },
};

const StatusIcon = ({ s }) =>
  s === 'ok' ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
    : s === 'warn' ? <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
      : <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />;

function Ring({ value }) {
  const r = 84, c = 2 * Math.PI * r;
  const color = value >= 85 ? '#34d399' : value >= 70 ? '#FF9B4A' : value >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div className="relative w-[200px] h-[200px]" data-testid="score-ring">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="14" />
        <circle cx="100" cy="100" r={r} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-bold text-white tabular-nums" data-testid="score-overall">{value}</span>
        <span className="text-[12px] text-neutral-500">/ 100</span>
      </div>
    </div>
  );
}

function Finding({ f, t, lang }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
      <StatusIcon s={f.status} />
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-white leading-snug">{f.title[lang] || f.title.en}</p>
        <p className="mt-1 text-[13px] text-neutral-400 leading-relaxed">{f.fix[lang] || f.fix.en}</p>
        <p className="mt-1.5 flex gap-2">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${f.impact === 'high' ? 'bg-red-500/15 text-red-300' : f.impact === 'medium' ? 'bg-amber-500/15 text-amber-300' : 'bg-white/[0.06] text-neutral-400'}`}>{t.impact[f.impact]}</span>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300">{t.effort[f.effort] || f.effort}</span>
        </p>
      </div>
    </div>
  );
}

export default function ScorePage() {
  const { lang } = useT();
  const t = TXT[lang === 'es' ? 'es' : 'en'];
  const L = lang === 'es' ? 'es' : 'en';
  const [form, setForm] = useState({ restaurant_name: '', website: '', city: '', email: '' });
  const [scanHuman, setScanHuman] = useState('');
  const [unlockHuman, setUnlockHuman] = useState('');
  const [phase, setPhase] = useState('form'); // form | scanning | teaser | full
  const [data, setData] = useState(null);
  const [unlockForm, setUnlockForm] = useState({ first_name: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showRecover, setShowRecover] = useState(false);
  const unlockRef = React.useRef(null);

  useEffect(() => {
    if (phase !== 'teaser' || !data?.scan_id) return undefined;
    if (sessionStorage.getItem(`kx_recover_${data.scan_id}`)) return undefined;
    const fire = () => {
      if (!sessionStorage.getItem(`kx_recover_${data.scan_id}`)) {
        sessionStorage.setItem(`kx_recover_${data.scan_id}`, '1');
        setShowRecover(true);
      }
    };
    const timer = setTimeout(fire, 22000);
    const onLeave = (e) => { if (e.clientY <= 0) fire(); };
    document.addEventListener('mouseleave', onLeave);
    return () => { clearTimeout(timer); document.removeEventListener('mouseleave', onLeave); };
  }, [phase, data?.scan_id]);

  const goUnlock = (email) => {
    if (email) setUnlockForm((p) => ({ ...p, email }));
    setShowRecover(false);
    setTimeout(() => unlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };

  const scan = async (e) => {
    e.preventDefault();
    if (!scanHuman) { setError(L === 'es' ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot."); return; }
    setError(''); setPhase('scanning');
    try {
      const r = await fetch(`${API}/api/score/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': scanHuman },
        body: JSON.stringify({ ...form, ref_code: getRef() || '', lang: L }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'scan failed');
      setData(d); setPhase('teaser');
    } catch (err) {
      setError(typeof err.message === 'string' && err.message.length < 200 ? err.message : t.err);
      setPhase('form');
    }
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!unlockHuman) { setError(L === 'es' ? 'Confirma que no eres un robot.' : "Please confirm you're not a robot."); return; }
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/score/unlock`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Human-Token': unlockHuman },
        body: JSON.stringify({ scan_id: data.scan_id, ...unlockForm }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'unlock failed');
      setData((prev) => ({ ...prev, ...d })); setPhase('full');
    } catch (err) {
      setError(typeof err.message === 'string' && err.message.length < 200 ? err.message : t.err);
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full h-12 rounded-lg bg-white/[0.05] border border-white/15 px-4 text-[15px] text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF9B4A]/70 transition-colors';

  return (
    <div className="bg-[#101317] min-h-screen">
      <SEO title="Restaurant Operating Score™ — Kitxens"
        description={L === 'es' ? 'Descubre en 30 segundos la puntuación operativa real de tu restaurante: visibilidad IA, web, menú y conversión.' : 'Get your restaurant\'s real operating score in 30 seconds: AI visibility, website, menu and conversion.'}
        path="/score" />
      <section className="max-w-6xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24">
        <p className="text-[#FF9B4A] text-xs font-bold tracking-[3px]">{t.kicker}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">{t.h1}</h1>
        <p className="mt-4 text-base text-neutral-400 max-w-2xl">{t.sub}</p>

        {phase === 'form' && (
          <div className="mt-10 grid lg:grid-cols-[minmax(0,1fr),auto] gap-12 items-start">
            <form onSubmit={scan} className="max-w-xl space-y-3 w-full" data-testid="score-form">
              <input required data-testid="score-name-input" className={inputCls} placeholder={t.name}
                value={form.restaurant_name} onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} />
              <input required data-testid="score-website-input" className={inputCls} placeholder={t.website}
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              <input data-testid="score-city-input" className={inputCls} placeholder={t.city}
                value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <input required type="email" data-testid="score-email-input" className={inputCls} placeholder={t.yourEmail}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              {error && <p className="text-red-400 text-[13px]" data-testid="score-error">{error}</p>}
              <HumanCheck dark onToken={setScanHuman} />
              <button type="submit" data-testid="score-submit-btn"
                className="w-full h-13 py-3.5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[15px] transition-colors inline-flex items-center justify-center gap-2">
                {t.cta} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <div className="hidden lg:block -mt-24">
              <PhoneSimulation lang={L} withCta={false} />
            </div>
          </div>
        )}

        {phase === 'scanning' && (
          <div className="mt-12 max-w-xl" data-testid="score-scanning">
            <p className="flex items-center gap-2 text-white font-semibold"><Loader2 className="w-5 h-5 animate-spin text-[#FF9B4A]" /> {t.scanning}</p>
            <div className="mt-4 space-y-2">
              {t.steps.map((s, i) => (
                <p key={s} className="text-[13.5px] text-neutral-500" style={{ animation: `fadeIn 0.5s ease ${i * 0.9}s both` }}>· {s}</p>
              ))}
            </div>
            <style>{'@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}'}</style>
          </div>
        )}

        {(phase === 'teaser' || phase === 'full') && data && (
          <div className="mt-12" data-testid="score-result">
            <div className="grid lg:grid-cols-[240px,1fr] gap-10 items-start">
              <div className="flex flex-col items-center">
                <Ring value={data.overall} />
                <p className="mt-3 text-center text-[14px] font-semibold text-white">{data.restaurant_name}</p>
                <p className="text-[12px] text-neutral-500 break-all text-center">{data.website}</p>
                <p className="mt-2 text-center text-[13px] text-[#FF9B4A] font-semibold" data-testid="score-grade">{t.grade(data.overall)}</p>
              </div>
              <div className="space-y-3">
                {Object.entries(t.labels).map(([k, label]) => {
                  const v = data.subscores?.[k];
                  return (
                    <div key={k} data-testid={`score-sub-${k}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-[13.5px] text-neutral-300 font-semibold">{label}</span>
                        {v === null || v === undefined
                          ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/[0.06] text-neutral-400 border border-white/10">{t.pending}</span>
                          : <span className="text-[14px] font-bold text-white tabular-nums">{v}</span>}
                      </div>
                      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                        {v !== null && v !== undefined && (
                          <div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 70 ? '#34d399' : v >= 40 ? '#FF9B4A' : '#f87171', transition: 'width 1s ease' }} />
                        )}
                      </div>
                      {(v === null || v === undefined) && <p className="mt-1 text-[11.5px] text-neutral-600">{t.pendingHint}</p>}
                    </div>
                  );
                })}
              </div>
            </div>

            {phase === 'teaser' && (
              <>
                <h2 className="mt-12 text-lg font-bold text-white">{t.topIssues}</h2>
                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  {(data.top_findings || []).map((f) => <Finding key={f.id} f={f} t={t} lang={L} />)}
                </div>
                <div ref={unlockRef} className="mt-8 rounded-2xl border border-[#FF9B4A]/30 bg-[#F77F11]/[0.07] p-6 lg:p-8">
                  <p className="flex items-center gap-2 text-white font-bold text-[17px]"><Lock className="w-4 h-4 text-[#FF9B4A]" /> {t.lockedTitle(data.findings_count)}</p>
                  <p className="mt-1.5 text-[13.5px] text-neutral-400">{t.lockedSub}</p>
                  <form onSubmit={unlock} className="mt-5 grid sm:grid-cols-[1fr,1.2fr,auto] gap-3" data-testid="score-unlock-form">
                    <input data-testid="score-unlock-name" className={inputCls} placeholder={t.yourName}
                      value={unlockForm.first_name} onChange={(e) => setUnlockForm({ ...unlockForm, first_name: e.target.value })} />
                    <input required type="email" data-testid="score-unlock-email" className={inputCls} placeholder={t.yourEmail}
                      value={unlockForm.email} onChange={(e) => setUnlockForm({ ...unlockForm, email: e.target.value })} />
                    <button type="submit" disabled={busy} data-testid="score-unlock-btn"
                      className="h-12 px-6 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[14px] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                      {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t.unlock}
                    </button>
                  </form>
                  <HumanCheck dark className="mt-3" onToken={setUnlockHuman} />
                  {error && <p className="mt-2 text-red-400 text-[13px]" data-testid="score-unlock-error">{error}</p>}
                </div>
              </>
            )}

            {phase === 'full' && (
              <div data-testid="score-full-report">
                <h2 className="mt-12 text-lg font-bold text-white">{t.actionPlan}</h2>
                <div className="mt-4 space-y-3">
                  {(data.action_plan || []).map((f, i) => (
                    <div key={f.id} className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-[#F77F11] text-white text-[13px] font-bold flex items-center justify-center shrink-0 mt-1">{i + 1}</span>
                      <div className="flex-1"><Finding f={f} t={t} lang={L} /></div>
                    </div>
                  ))}
                </div>
                <RoiEngine scanId={data.scan_id} lang={L} />
                <h2 className="mt-10 text-lg font-bold text-white">{t.fullReport} ({(data.findings || []).length})</h2>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                  {(data.findings || []).map((f) => <Finding key={f.id} f={f} t={t} lang={L} />)}
                </div>
                <div className="mt-10 rounded-2xl bg-[#F77F11] p-6 lg:p-8">
                  <p className="text-white font-bold text-xl">{t.finalCta}</p>
                  <p className="mt-1 text-[#7c3d02] text-[13.5px] font-semibold">{t.finalSub}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a data-testid="score-cta-whatsapp" href={`https://api.whatsapp.com/send/?phone=14085901102&text=${encodeURIComponent(`Hi Kitxens — my Restaurant Operating Score is ${data.overall}/100 (${data.restaurant_name}). I want to improve it.`)}`}
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1B1F24] text-white text-[13.5px] font-bold hover:opacity-90 transition-opacity">{t.talk}</a>
                    <a data-testid="score-cta-audit" href="/free-audit" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-[#1B1F24] text-[13.5px] font-bold hover:opacity-90 transition-opacity">{t.audit}</a>
                  </div>
                </div>
                <button onClick={() => { setPhase('form'); setData(null); setForm({ restaurant_name: '', website: '', city: '' }); }}
                  className="mt-6 text-[13px] text-neutral-500 hover:text-white transition-colors" data-testid="score-again-btn">↻ {t.again}</button>
              </div>
            )}
          </div>
        )}

        {showRecover && phase === 'teaser' && data && (
          <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="score-recover-modal"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowRecover(false); }}>
            <div className="w-full max-w-md rounded-3xl bg-[#1B1F24] border border-white/10 p-7 shadow-2xl" style={{ animation: 'fadeIn .25s ease both' }}>
              <p className="text-[#FF9B4A] text-[11px] font-bold tracking-[2.5px] uppercase">{L === 'es' ? 'Antes de irte' : 'Before you go'}</p>
              <h3 className="mt-2 text-[21px] font-bold text-white leading-snug">
                {L === 'es'
                  ? <>Tu reporte completo está listo — {data.findings_count} hallazgos te esperan</>
                  : <>Your full report is ready — {data.findings_count} findings are waiting</>}
              </h3>
              <p className="mt-2 text-[13.5px] text-neutral-400 leading-relaxed">
                {L === 'es'
                  ? `${data.restaurant_name} obtuvo ${data.overall}/100. Déjanos tu email y desbloquea gratis el plan de acción completo con el detalle de cada hallazgo.`
                  : `${data.restaurant_name} scored ${data.overall}/100. Leave your email and unlock the full action plan with every finding, free.`}
              </p>
              <form onSubmit={(e) => { e.preventDefault(); goUnlock(e.target.elements.rec_email.value); }} className="mt-5 flex gap-2">
                <input name="rec_email" required type="email" data-testid="score-recover-email"
                  placeholder={t.yourEmail} className="flex-1 h-11 rounded-lg bg-white/[0.06] border border-white/15 px-3.5 text-[14px] text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF9B4A]/70" />
                <button type="submit" data-testid="score-recover-cta"
                  className="h-11 px-5 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[13.5px] transition-colors whitespace-nowrap">
                  {L === 'es' ? 'Desbloquear gratis' : 'Unlock free'}
                </button>
              </form>
              <button onClick={() => setShowRecover(false)} data-testid="score-recover-dismiss"
                className="mt-3 text-[12px] text-neutral-500 hover:text-neutral-300 transition-colors">
                {L === 'es' ? 'Ahora no, seguir viendo el resumen' : 'Not now, keep viewing the summary'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
